// ── Single definition of "is this website connected?" ────────────────────────
//
// Used by the Overview "My Websites" card, the Websites tab Status column and
// the notification bell, so the three can never disagree.
//
// SOURCE OF TRUTH: `websites.last_connected_at`, stamped by the admin server
// (migration 008) every time the plugin authenticates for this site — the
// moment the API Key and Site Code are both saved correctly and the plugin
// talks to us. The plugin's "Test Server Connection" button goes through the
// same middleware, so this lands during setup.
//
// ── WHY `has_generations` IS GONE ───────────────────────────────────────────
// The rule used to OR the timestamp with "has this site ever generated
// content". That second signal was introduced as a rollout cushion, but a
// stale comment in useWebsitesWithStatus claiming the server work "has been
// reverted" led to the timestamp branch being deleted — leaving generations as
// the ONLY signal. That is what produced both reported bugs:
//
//   • A freshly connected site read "Not Connected" until its first article.
//   • A site whose API key had been REMOVED still read Active, because
//     generation history is a historical fact that can never go false.
//
// The second one is unfixable in principle with that signal. "Has ever
// generated" answers a different question from "can I use the plugin right
// now", and status is the second question.
//
// ── THE STALENESS WINDOW, AND WHY IT IS CONDITIONAL ─────────────────────────
// Removing an API key is only observable as an ABSENCE of contact, so a site
// that stops checking in has to eventually read "Not Connected". Hence the
// window below.
//
// But it applies ONLY to sites that have proved they check in, which is what
// `heartbeat_at` records: the plugin now pings hourly, and the server stamps
// that field when it does. A site running an older plugin build never pings
// while idle, so applying a window to it would report a perfectly healthy site
// as dead — which is precisely the objection the previous author raised when
// they ruled a window out as a "deliberate non-goal". That objection was
// correct FOR A PLUGIN THAT DOES NOT HEARTBEAT, and it still governs those
// installs. It stops applying once the plugin checks in on its own.
//
// So: no heartbeat seen → "connected once" stays Active, exactly as before.
// Heartbeat seen → silence past the window means gone.
//
// NOTE: `site_code` is NOT a connection signal — it is minted when the site is
// ADDED, before the plugin exists. Do not be tempted.
//
// NOTE: `website.active` is not a connection flag either — it is the
// soft-delete flag, and the list endpoint already filters to active = true, so
// it is always true here.

export const STATUS_ACTIVE = 'Active';
export const STATUS_NOT_CONNECTED = 'Not Connected';

/**
 * How long a heartbeating site may stay silent before it reads disconnected.
 *
 * The plugin pings hourly, so 24 misses. That margin is deliberate: WP-Cron is
 * traffic-driven, not a real scheduler, and a low-traffic site can easily skip
 * several hours. A tight window would make the chip flap on exactly the quiet
 * sites this is meant to serve.
 *
 * Removing an API key is reported immediately by the plugin anyway — this is
 * the backstop for the cases that CANNOT report themselves: the plugin folder
 * deleted, the site taken offline, credentials changed in the database.
 */
export const CONNECTION_STALE_HOURS = 24;

const STALE_MS = CONNECTION_STALE_HOURS * 60 * 60 * 1000;

/** Milliseconds for a timestamp in any shape the API might send, else null. */
const timeOf = (value) => {
  if (value == null) return null;
  const ms = value instanceof Date ? value.getTime() : new Date(value).getTime();
  return Number.isNaN(ms) ? null : ms;
};

/**
 * True when the plugin is authenticating for this site.
 *
 * @param website  a row from the websites payload
 * @param now      injectable for tests; the window is the only use of it
 */
export const isWebsiteConnected = (website, now = Date.now()) => {
  const last = timeOf(website?.last_connected_at);
  if (last === null) return false;

  // Older plugin build: it only calls us when it has work to do, so silence
  // carries no information and the timestamp alone has to stand.
  if (timeOf(website?.heartbeat_at) === null) return true;

  // A timestamp in the future means clock skew between us and the server, not
  // a disconnection. Only elapsed time counts against a site.
  return now - last <= STALE_MS;
};

export const websiteStatusLabel = (website) =>
  (isWebsiteConnected(website) ? STATUS_ACTIVE : STATUS_NOT_CONNECTED);

/**
 * Order comparator: connected sites first, everything else after.
 *
 * Client feedback (Aug 8) on BOTH the Overview card and the Websites tab. It
 * matters most on Overview, which previews only the first three rows — in
 * server order (created_at DESC) a handful of recently-added, unconnected sites
 * could push every working site out of view.
 *
 * Pass to `.sort()` on a COPY: Array.prototype.sort mutates, and these lists are
 * React state. The sort is stable (ES2019+), so `created_at DESC` is preserved
 * within each group and rows never shuffle between renders.
 */
export const compareByConnection = (a, b) =>
  Number(isWebsiteConnected(b)) - Number(isWebsiteConnected(a));
